import { useState, useCallback, useRef, useEffect } from "react";
import {
  updateStatus,
  updateTextColumn,
  updateDateColumn,
  clearDateColumn,
  createItem,
  addFileToColumn,
  clearFileColumn,
  deleteItem,
  updateItemName,
  updateDropdownColumn,
  updateStatusColumn,
  getItemById,
} from "../services/mondayApiService";
import { useMondayContext } from "./useMondayContext";
import { throttle } from "../utils/debounceThrottle";
import { BOARD_CONFIG } from "../constants/boardConfig";
import { DONE_LABELS } from "../constants/statusLabels";
import {
  WEEKLY_CHECKS_BOARD_ID,
  METER_READINGS_BOARD_ID,
  CLEANING_CHECKS_BOARD_ID,
  PROPERTIES_BOARD_ID,
  REPORTS_BOARD_ID,
  FILE_COLUMN_ID,
  REPORTS_PROPERTY_COLUMN_ID,
  TASKS_BOARD_ID,
  TASKS_PROPERTY_COLUMN_ID,
  TASKS_DUE_DATE_COLUMN_ID,
  TASKS_DESCRIPTION_COLUMN_ID_FULL,
  WATER_COLUMN_ID,
  ELECTRICITY_COLUMN_ID,
  GAS_COLUMN_ID,
  METER_READINGS_NOTES_COLUMN_ID,
  ELECTRICITY_DATE_COLUMN_ID,
  WATER_DATE_COLUMN_ID,
  GAS_DATE_COLUMN_ID,
  METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
  LAST_COMPLETED_DATE_COLUMN_ID,
  WEEKLY_CHECKS_TYPE_COLUMN_ID,
  DEFAULT_PERSON_COLUMN_ID,
  TIME_SPENT_COLUMN_ID,
  TIME_TRACKING_COLUMN_ID,
  TASKS_FILE_COLUMN_ID,
  TASKS_COMMENTS_COLUMN_ID,
} from "../constants/columnIds";
import { normalizeDateOnly } from "../utils/dateUtils";
import { getDayAbbreviation } from "../utils/dateUtils";
import { compressImageFile } from "../utils/imageUtils";
import { parseColumnValue, parseFileColumnValue } from "../utils/dataParsingUtils";
import { isTaskDone } from "../utils/dataParsingUtils";

export function useTaskActions({
  viewType,
  boardId,
  config,
  handleRefresh,
  personColumnId,
  toast,
  currentDate,
  currentWeekStart,
}) {
  // Read from context instead of props
  const {
    rawItems,
    tasksByProperty,
    updateTasksByProperty,
    availablePeople,
    personColumnIdMap,
    propertyDetailsMap,
    updateRawItems,
  } = useMondayContext();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportImages, setReportImages] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);
  const [confirmingTask, setConfirmingTask] = useState(false);
  const [waterReading, setWaterReading] = useState([]); // Array of files for water meter photos
  const [gasReading, setGasReading] = useState([]); // Array of files for gas meter photos
  const [electricityReading, setElectricityReading] = useState("");
  // State for tracking saved reading dates
  const [readingDates, setReadingDates] = useState({
    electricity: null,
    water: null,
    gas: null,
  });
  // State for tracking saving status of individual readings
  const [savingReading, setSavingReading] = useState({
    electricity: false,
    water: false,
    gas: false,
  });
  const [taskImages, setTaskImages] = useState([]); // Array of files for TASKS board images
  const [taskComments, setTaskComments] = useState(""); // Comments for TASKS board
  // Progress tracking for image operations
  const [imageProgress, setImageProgress] = useState({
    compressing: false,
    uploading: false,
    total: 0,
    completed: 0,
    currentFile: null,
  });
  // PHASE 2: State for showing confirmation visual feedback
  const [showConfirmedState, setShowConfirmedState] = useState(false);
  // PHASE 3: Progress tracking for Meter Readings confirmation uploads
  const [confirmationProgress, setConfirmationProgress] = useState({
    uploading: false,
    total: 0,
    completed: 0,
    currentOperation: null,
  });
  // Ref to track popup close timeout for proper cleanup
  const popupCloseTimeoutRef = useRef(null);
  // Ref to track if component is mounted (prevents state updates on unmounted component)
  const isMountedRef = useRef(true);
  
  // Cleanup effect: Clear timeout on unmount and track mount status
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      // Clear any pending popup close timeout on unmount
      if (popupCloseTimeoutRef.current) {
        clearTimeout(popupCloseTimeoutRef.current);
        popupCloseTimeoutRef.current = null;
      }
    };
  }, []);
  
  const [showNewTaskPopup, setShowNewTaskPopup] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newTaskDate, setNewTaskDate] = useState("");
  const [newTaskPerson, setNewTaskPerson] = useState("");
  const [newTaskProperty, setNewTaskProperty] = useState("");
  const [newTaskPropertySearch, setNewTaskPropertySearch] = useState("");
  const [newTaskTimeTracking, setNewTaskTimeTracking] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [timeSpent, setTimeSpent] = useState("");
  // State for displaying completed meter reading data
  const [completedMeterReadingData, setCompletedMeterReadingData] = useState({
    electricity: "",
    water: [],
    gas: [],
    notes: "",
  });
  // State for displaying completed TASKS data
  const [completedTaskData, setCompletedTaskData] = useState({
    images: [],
    comments: "",
  });
  // State for Cleaning Checks extras
  const [extras, setExtras] = useState("");
  const [savingExtras, setSavingExtras] = useState(false);
  // State for editing TASKS
  const [isEditingTask, setIsEditingTask] = useState(false);
  const [editTaskName, setEditTaskName] = useState("");
  const [editTaskDescription, setEditTaskDescription] = useState("");
  const [editTaskDate, setEditTaskDate] = useState("");
  const [editTaskPerson, setEditTaskPerson] = useState("");
  const [editTaskTimeTracking, setEditTaskTimeTracking] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);
  // State for property details (Keybox, Alarm, Door/Gate Code, Location)
  const [propertyDetails, setPropertyDetails] = useState({
    keybox: "",
    alarm: "",
    doorGateCode: "",
    location: null,
  });

  // Memoize handlers to prevent unnecessary re-renders
  const handleTaskClick = useCallback((task, propertyName) => {
    // If this is a TASKS task, initialize edit state
    if (task.boardId === TASKS_BOARD_ID) {
      // Find the raw item from context to get full data (description, timeTracking, etc.)
      const rawItem = rawItems.find(item => item.id === task.id && item._boardId === TASKS_BOARD_ID);
      
      // Get description from task or rawItem
      let description = task.description || "";
      if (!description && rawItem && rawItem.column_values) {
        description = parseColumnValue(rawItem.column_values, TASKS_DESCRIPTION_COLUMN_ID_FULL) || "";
      }
      
      // Get time tracking from task or rawItem
      let timeTracking = task.timeTracking;
      if (!timeTracking && rawItem && rawItem.column_values) {
        const timeTrackingCol = rawItem.column_values.find((c) => c.id === TIME_TRACKING_COLUMN_ID);
        let timeTrackingText = null;
        
        if (timeTrackingCol) {
          timeTrackingText = timeTrackingCol.text;
          if (!timeTrackingText && timeTrackingCol.value) {
            try {
              const parsed = JSON.parse(timeTrackingCol.value);
              if (parsed.label) {
                timeTrackingText = parsed.label;
              } else if (typeof parsed === 'string') {
                timeTrackingText = parsed;
              }
            } catch (e) {
              // Not JSON, continue
            }
          }
        }
        timeTracking = timeTrackingText && timeTrackingText.toLowerCase().trim() === "yes" ? "yes" : "no";
      }
      
      setEditTaskName(task.name || "");
      setEditTaskDescription(description);
      
      // Initialize date - use task.date if available, format as YYYY-MM-DD
      if (task.date) {
        const dateStr = typeof task.date === 'string' 
          ? task.date.split('T')[0] 
          : task.date instanceof Date 
            ? task.date.toISOString().split('T')[0]
            : "";
        setEditTaskDate(dateStr);
      } else {
        setEditTaskDate("");
      }
      
      // Initialize person - use first person name or personText, or fetch from rawItem
      let personValue = "";
      if (task.personNames && task.personNames.length > 0) {
        personValue = task.personNames[0];
      } else if (task.personText) {
        personValue = task.personText;
      } else if (rawItem && rawItem.column_values) {
        // Fallback: extract from rawItem if not in task object
        const tasksPersonColumnId = personColumnIdMap?.[TASKS_BOARD_ID] || personColumnId || DEFAULT_PERSON_COLUMN_ID;
        const personCol = rawItem.column_values.find((c) => c.id === tasksPersonColumnId);
        if (personCol) {
          if (personCol.text) {
            const textLabels = personCol.text.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);
            if (textLabels.length > 0) {
              personValue = textLabels[0];
            }
          }
          if (!personValue && personCol.value && personCol.value !== "null" && personCol.value !== "") {
            try {
              const parsed = JSON.parse(personCol.value);
              if (parsed.labels && Array.isArray(parsed.labels) && parsed.labels.length > 0) {
                personValue = parsed.labels[0];
              } else if (parsed.label) {
                personValue = parsed.label;
              }
            } catch (e) {
              // Not JSON, continue
            }
          }
        }
      }
      setEditTaskPerson(personValue);
      
      // Initialize time tracking
      setEditTaskTimeTracking(timeTracking && String(timeTracking).toLowerCase().trim() === "yes");
      
      setIsEditingTask(false);
    }
    
    // If this is a Cleaning Checks task, initialize extras from task data
    if (task.boardId === CLEANING_CHECKS_BOARD_ID) {
      setExtras(task.extras || "");
    }
    
    // If this is a Meter Readings task, fetch the meter reading data (both completed and incomplete)
    if (task.boardId === METER_READINGS_BOARD_ID) {
      // Find the raw item from context
      const rawItem = rawItems.find(item => item.id === task.id && item._boardId === METER_READINGS_BOARD_ID);
      
      if (rawItem && rawItem.column_values) {
        // Parse electricity reading (text column)
        const electricityText = parseColumnValue(rawItem.column_values, ELECTRICITY_COLUMN_ID) || "";
        
        // Parse water meter photos (file column)
        const waterFiles = parseFileColumnValue(rawItem.column_values, WATER_COLUMN_ID);
        
        // Parse gas meter photos (file column)
        const gasFiles = parseFileColumnValue(rawItem.column_values, GAS_COLUMN_ID);
        
        // Parse notes (text column)
        const notesText = parseColumnValue(rawItem.column_values, METER_READINGS_NOTES_COLUMN_ID) || "";
        
        // Parse reading dates - try multiple methods to ensure we get the date
        const electricityDateCol = rawItem.column_values.find(c => c.id === ELECTRICITY_DATE_COLUMN_ID);
        const waterDateCol = rawItem.column_values.find(c => c.id === WATER_DATE_COLUMN_ID);
        const gasDateCol = rawItem.column_values.find(c => c.id === GAS_DATE_COLUMN_ID);
        
        // Try parseColumnValue first, then try direct parsing if that fails
        let electricityDateText = parseColumnValue(rawItem.column_values, ELECTRICITY_DATE_COLUMN_ID);
        let waterDateText = parseColumnValue(rawItem.column_values, WATER_DATE_COLUMN_ID);
        let gasDateText = parseColumnValue(rawItem.column_values, GAS_DATE_COLUMN_ID);
        
        // If parseColumnValue didn't work, try parsing directly from the column
        if (!electricityDateText && electricityDateCol) {
          if (electricityDateCol.value && electricityDateCol.value !== "null" && electricityDateCol.value !== "") {
            try {
              const parsed = JSON.parse(electricityDateCol.value);
              if (parsed.date) {
                electricityDateText = parsed.date;
              }
            } catch (e) {
              // If JSON parse fails, try text
              if (electricityDateCol.text) {
                const dateMatch = electricityDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
                if (dateMatch) {
                  electricityDateText = dateMatch[0];
                }
              }
            }
          } else if (electricityDateCol.text) {
            const dateMatch = electricityDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              electricityDateText = dateMatch[0];
            }
          }
        }
        
        if (!waterDateText && waterDateCol) {
          if (waterDateCol.value && waterDateCol.value !== "null" && waterDateCol.value !== "") {
            try {
              const parsed = JSON.parse(waterDateCol.value);
              if (parsed.date) {
                waterDateText = parsed.date;
              }
            } catch (e) {
              if (waterDateCol.text) {
                const dateMatch = waterDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
                if (dateMatch) {
                  waterDateText = dateMatch[0];
                }
              }
            }
          } else if (waterDateCol.text) {
            const dateMatch = waterDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              waterDateText = dateMatch[0];
            }
          }
        }
        
        if (!gasDateText && gasDateCol) {
          if (gasDateCol.value && gasDateCol.value !== "null" && gasDateCol.value !== "") {
            try {
              const parsed = JSON.parse(gasDateCol.value);
              if (parsed.date) {
                gasDateText = parsed.date;
              }
            } catch (e) {
              if (gasDateCol.text) {
                const dateMatch = gasDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
                if (dateMatch) {
                  gasDateText = dateMatch[0];
                }
              }
            }
          } else if (gasDateCol.text) {
            const dateMatch = gasDateCol.text.match(/\d{4}-\d{2}-\d{2}/);
            if (dateMatch) {
              gasDateText = dateMatch[0];
            }
          }
        }
        
        const electricityDate = electricityDateText ? normalizeDateOnly(electricityDateText) : null;
        const waterDate = waterDateText ? normalizeDateOnly(waterDateText) : null;
        const gasDate = gasDateText ? normalizeDateOnly(gasDateText) : null;
        
        const today = normalizeDateOnly(new Date());
        
        // If reading date is today, populate the reading values into input fields
        if (electricityDate && today && electricityDate.getTime() === today.getTime()) {
          setElectricityReading(electricityText);
        } else {
          setElectricityReading("");
        }
        
        // Note: For water and gas, we can't populate file inputs directly from URLs
        // But we can show them in completedMeterReadingData which is already being set
        // The checkmark will show based on the date
        
        setCompletedMeterReadingData({
          electricity: electricityText,
          water: waterFiles,
          gas: gasFiles,
          notes: notesText,
        });
        
        setReadingDates({
          electricity: electricityDate,
          water: waterDate,
          gas: gasDate,
        });
      } else {
        // Reset if no data found
        setCompletedMeterReadingData({
          electricity: "",
          water: [],
          gas: [],
          notes: "",
        });
        setReadingDates({
          electricity: null,
          water: null,
          gas: null,
        });
        setElectricityReading("");
        setWaterReading([]);
        setGasReading([]);
      }
    } else {
      // Reset for non-meter-reading tasks
      setCompletedMeterReadingData({
        electricity: "",
        water: [],
        gas: [],
        notes: "",
      });
      setReadingDates({
        electricity: null,
        water: null,
        gas: null,
      });
      setElectricityReading("");
      setWaterReading([]);
      setGasReading([]);
    }
    
    // If this is a TASKS task, fetch the completed task data (images and comments)
    if (task.boardId === TASKS_BOARD_ID) {
      // Find the raw item from context
      const rawItem = rawItems.find(item => item.id === task.id && item._boardId === TASKS_BOARD_ID);
      
      if (rawItem && rawItem.column_values) {
        // Parse task images (file column)
        const taskImagesFiles = parseFileColumnValue(rawItem.column_values, TASKS_FILE_COLUMN_ID);
        
        // Parse comments (text column)
        const commentsText = parseColumnValue(rawItem.column_values, TASKS_COMMENTS_COLUMN_ID) || "";
        
        setCompletedTaskData({
          images: taskImagesFiles,
          comments: commentsText,
        });
      } else {
        // Reset if no data found
        setCompletedTaskData({
          images: [],
          comments: "",
        });
      }
    } else {
      // Reset for non-TASKS tasks
      setCompletedTaskData({
        images: [],
        comments: "",
      });
    }
    
    // Get property details from cache if property ID is available
    if (task.propertyItemIds && task.propertyItemIds.length > 0) {
      const propertyId = task.propertyItemIds[0]; // Use first property ID
      const propertyIdStr = String(propertyId);
      const cachedDetails = propertyDetailsMap[propertyIdStr] || propertyDetailsMap[Number(propertyId)];
      
      if (cachedDetails) {
        setPropertyDetails(cachedDetails);
      } else {
        setPropertyDetails({ keybox: "", alarm: "", doorGateCode: "", location: null });
      }
    } else {
      setPropertyDetails({ keybox: "", alarm: "", doorGateCode: "", location: null });
    }
    
    setSelectedTask({ ...task, propertyName });
    setShowPopup(true);
  }, [rawItems, personColumnIdMap, propertyDetailsMap]);

  const handleClosePopup = useCallback(() => {
    // Clear any pending popup close timeout
    if (popupCloseTimeoutRef.current) {
      clearTimeout(popupCloseTimeoutRef.current);
      popupCloseTimeoutRef.current = null;
    }
    
    // Only update state if component is still mounted
    if (!isMountedRef.current) return;
    
    setShowPopup(false);
    setSelectedTask(null);
    setReportText("");
    setReportImages([]);
    setWaterReading([]);
    setGasReading([]);
    setElectricityReading("");
    setTimeSpent("");
    setConfirmingTask(false);
    setExtras("");
    setSavingExtras(false);
    setIsEditingTask(false);
    setEditTaskName("");
    setPropertyDetails({ keybox: "", alarm: "", doorGateCode: "", location: null });
    setEditTaskDescription("");
    setSavingTask(false);
    setDeletingTask(false);
    setCompletedMeterReadingData({
      electricity: "",
      water: [],
      gas: [],
    });
    setCompletedTaskData({
      images: [],
      comments: "",
    });
    setReadingDates({
      electricity: null,
      water: null,
      gas: null,
    });
    setSavingReading({
      electricity: false,
      water: false,
      gas: false,
    });
  }, []);

  const handleImageUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    try {
      setImageProgress({ compressing: true, uploading: false, total: files.length, completed: 0, currentFile: null });
      
      const compressedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setImageProgress(prev => ({ ...prev, completed: i, currentFile: file.name }));
        const compressed = await compressImageFile(file, 750);
        compressedFiles.push(compressed);
      }
      
      setReportImages((prev) => [...prev, ...compressedFiles]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.success(`Added ${files.length} image${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error("Error compressing report images, using originals:", err);
      setReportImages((prev) => [...prev, ...files]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.warning("Image compression failed, using original files");
    }
  }, [toast]);

  const removeImage = useCallback((index) => {
    setReportImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleWaterPhotoUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    try {
      setImageProgress({ compressing: true, uploading: false, total: files.length, completed: 0, currentFile: null });
      
      const compressedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setImageProgress(prev => ({ ...prev, completed: i, currentFile: file.name }));
        const compressed = await compressImageFile(file, 750);
        compressedFiles.push(compressed);
      }
      
      setWaterReading((prev) => [...prev, ...compressedFiles]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.success(`Added ${files.length} water meter photo${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error("Error compressing water meter photos, using originals:", err);
      setWaterReading((prev) => [...prev, ...files]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.warning("Image compression failed, using original files");
    }
  }, [toast]);

  const removeWaterPhoto = useCallback((index) => {
    setWaterReading((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleGasPhotoUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    try {
      setImageProgress({ compressing: true, uploading: false, total: files.length, completed: 0, currentFile: null });
      
      const compressedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setImageProgress(prev => ({ ...prev, completed: i, currentFile: file.name }));
        const compressed = await compressImageFile(file, 750);
        compressedFiles.push(compressed);
      }
      
      setGasReading((prev) => [...prev, ...compressedFiles]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.success(`Added ${files.length} gas meter photo${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error("Error compressing gas meter photos, using originals:", err);
      setGasReading((prev) => [...prev, ...files]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.warning("Image compression failed, using original files");
    }
  }, [toast]);

  const removeGasPhoto = useCallback((index) => {
    setGasReading((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleTaskPhotoUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) return;

    try {
      setImageProgress({ compressing: true, uploading: false, total: files.length, completed: 0, currentFile: null });
      
      const compressedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setImageProgress(prev => ({ ...prev, completed: i, currentFile: file.name }));
        const compressed = await compressImageFile(file, 750);
        compressedFiles.push(compressed);
      }
      
      setTaskImages((prev) => [...prev, ...compressedFiles]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.success(`Added ${files.length} image${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error("Error compressing task photos, using originals:", err);
      setTaskImages((prev) => [...prev, ...files]);
      setImageProgress({ compressing: false, uploading: false, total: 0, completed: 0, currentFile: null });
      if (toast) toast.warning("Image compression failed, using original files");
    }
  }, [toast]);

  const removeTaskPhoto = useCallback((index) => {
    setTaskImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCloseNewTaskPopup = useCallback(() => {
    setShowNewTaskPopup(false);
    setNewTaskTitle("");
    setNewTaskDescription("");
    setNewTaskDate("");
    setNewTaskPerson("");
    setNewTaskProperty("");
    setNewTaskPropertySearch("");
    setNewTaskTimeTracking(false);
    setCreatingTask(false);
  }, []);

  const handleToggle = async ({ propertyName, taskId }) => {
    const propertyTasks = tasksByProperty[propertyName] || [];
    const task = propertyTasks.find((t) => t.id === taskId);
    if (!task) return;

    const newDone = !task.done;

    // Optimistic UI update
    const updated = { ...tasksByProperty };
    updated[propertyName] = propertyTasks.map((t) =>
      t.id === taskId ? { ...t, done: newDone } : t
    );
    updateTasksByProperty(updated);

    try {
      await updateStatus({
        boardId,
        itemId: taskId,
        statusColumnId: config.statusColumnId,
        done: newDone,
      });
    } catch (err) {
      console.error(err);
      // Revert on error
      const reverted = { ...tasksByProperty };
      reverted[propertyName] = propertyTasks;
      updateTasksByProperty(reverted);
    }
  };

  // Throttle confirm task handler to prevent rapid API calls (max once per 1000ms)
  const handleConfirmTaskInternal = useCallback(async () => {
    if (!selectedTask || confirmingTask) return;

    const taskBoardId = selectedTask.boardId || boardId;
    const taskConfig = selectedTask.boardId ? BOARD_CONFIG[selectedTask.boardId] : config;
    const propertyName = selectedTask.propertyName;
    
    if (!taskBoardId || !taskConfig || !propertyName) return;

    // For Meter Readings: Wait for any individual saves to complete to prevent conflicts
    if (taskBoardId === METER_READINGS_BOARD_ID) {
      const isSavingAny = savingReading.electricity || savingReading.water || savingReading.gas;
      if (isSavingAny) {
        if (toast) toast.warning("Please wait for individual readings to finish saving before confirming.");
        return;
      }
    }

    // Check if this is a TASKS board task with Time Tracking enabled
    // If Time Tracking is "yes", require time spent
    if (taskBoardId === TASKS_BOARD_ID && selectedTask.timeTracking === "yes") {
      if (!timeSpent || timeSpent.trim() === "" || isNaN(parseInt(timeSpent.trim()))) {
        if (toast) toast.warning("Please enter time spent in minutes. Time tracking is enabled for this task.");
        setConfirmingTask(false);
        return;
      }
    }

    setConfirmingTask(true);

    // PHASE 3: Initialize progress tracking for Meter Readings
    if (taskBoardId === METER_READINGS_BOARD_ID) {
      setConfirmationProgress({
        uploading: false,
        total: 0,
        completed: 0,
        currentOperation: null,
      });
    }

    // Store original state for potential rollback
    const originalState = JSON.parse(JSON.stringify(tasksByProperty));

    // Optimistic UI update - mark task as done immediately
    const updated = { ...tasksByProperty };
    
    if (viewType === "daily") {
      // Daily view: tasks are grouped by property
      if (updated[propertyName]) {
        updated[propertyName] = updated[propertyName].map((t) =>
          t.id === selectedTask.id ? { ...t, done: true } : t
        );
      }
    } else {
      // Weekly view: tasks are grouped by day, then property
      // Use task's date to find the correct day key
      let dayKey = null;
      if (selectedTask.date) {
        dayKey = getDayAbbreviation(selectedTask.date);
      } else {
        // Fallback: search through all days
        const dayKeys = Object.keys(updated);
        for (const key of dayKeys) {
          if (updated[key] && typeof updated[key] === "object" && updated[key][propertyName]) {
            const taskExists = updated[key][propertyName].some((t) => t.id === selectedTask.id);
            if (taskExists) {
              dayKey = key;
              break;
            }
          }
        }
      }
      
      if (dayKey && updated[dayKey] && typeof updated[dayKey] === "object" && updated[dayKey][propertyName]) {
        updated[dayKey] = {
          ...updated[dayKey],
          [propertyName]: updated[dayKey][propertyName].map((t) =>
            t.id === selectedTask.id ? { ...t, done: true } : t
          ),
        };
      }
    }
    
    updateTasksByProperty(updated);

    // PHASE 2: Show visual confirmation state briefly before closing
    // Set a flag to show "Confirmed ✓" state in popup for 300ms
    // This provides clear visual feedback before popup closes
    
    // OPTIMIZATION: Close popup immediately after optimistic update for instant feedback
    // Show success toast immediately
    if (toast) toast.success("Task confirmed!");
    
    // Brief delay to show confirmation state, then close popup
    // Store timeout ID for proper cleanup
    popupCloseTimeoutRef.current = setTimeout(() => {
      popupCloseTimeoutRef.current = null;
      handleClosePopup();
    }, 300);

    try {
      // Determine status column ID - use override for Cleaning Checks, otherwise use config
      const statusColumnId = selectedTask.statusColumnIdOverride || taskConfig.statusColumnId;
      
      // Update status to Done in Monday.com
      await updateStatus({
        boardId: taskBoardId,
        itemId: selectedTask.id,
        statusColumnId: statusColumnId,
        done: true,
      });

      // Also update the status in rawItems so it persists when tasks are reprocessed
      const updatedRawItems = rawItems.map(item => {
        if (item.id === selectedTask.id && item._boardId === taskBoardId) {
          const updatedItem = { ...item };
          const updatedColumnValues = [...(item.column_values || [])];
          const statusColumnIndex = updatedColumnValues.findIndex(col => col.id === statusColumnId);
          
          if (statusColumnIndex >= 0) {
            // Update existing status column
            updatedColumnValues[statusColumnIndex] = {
              ...updatedColumnValues[statusColumnIndex],
              text: "Done",
              value: JSON.stringify({ label: "Done", index: 0 }),
            };
          } else {
            // Add status column if it doesn't exist
            updatedColumnValues.push({
              id: statusColumnId,
              type: "status",
              text: "Done",
              value: JSON.stringify({ label: "Done", index: 0 }),
            });
          }
          
          updatedItem.column_values = updatedColumnValues;
          return updatedItem;
        }
        return item;
      });
      updateRawItems(updatedRawItems);

      // If this is a TASKS task, update columns and images in parallel where possible
      if (taskBoardId === TASKS_BOARD_ID) {
        const tasksFileColumnId = TASKS_FILE_COLUMN_ID;
        const tasksCommentsColumnId = TASKS_COMMENTS_COLUMN_ID;

        // Prepare independent column updates (can run in parallel)
        const columnUpdatePromises = [];

        // Add time spent update if provided
        if (timeSpent && timeSpent.trim() !== "") {
          const timeSpentValue = timeSpent.trim();
          if (!isNaN(parseInt(timeSpentValue))) {
            columnUpdatePromises.push(
              updateTextColumn({
                boardId: taskBoardId,
                itemId: selectedTask.id,
                columnId: TIME_SPENT_COLUMN_ID,
                value: timeSpentValue,
              }).catch(err => {
                console.error("Error updating time spent:", err);
                throw err; // Re-throw to be caught by Promise.allSettled
              })
            );
          }
        }

        // Add comments update if provided
        if (taskComments.trim() !== "") {
          columnUpdatePromises.push(
            updateTextColumn({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              columnId: tasksCommentsColumnId,
              value: taskComments.trim(),
            }).catch(err => {
              console.error("Error updating task comments:", err);
              throw err; // Re-throw to be caught by Promise.allSettled
            })
          );
        }

        // Execute all column updates in parallel
        if (columnUpdatePromises.length > 0) {
          await Promise.allSettled(columnUpdatePromises);
        }

        // Replace task images (clear existing, then upload new) if any were added
        if (taskImages.length > 0) {
          try {
            await clearFileColumn({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              columnId: tasksFileColumnId,
            });

            // Upload all images in parallel
            const uploadPromises = taskImages.map(file =>
              addFileToColumn({
                itemId: selectedTask.id,
                columnId: tasksFileColumnId,
                file: file,
              }).catch(fileErr => {
                console.error("Error uploading task image:", file.name, fileErr);
                // Return null for failed uploads so Promise.allSettled continues
                return null;
              })
            );

            await Promise.allSettled(uploadPromises);
          } catch (clearErr) {
            console.error("Error clearing existing task images:", clearErr);
          }
        }
      }

      // If this is a Meter Readings task, execute the automation logic and meter reading updates
      if (taskBoardId === METER_READINGS_BOARD_ID) {
        try {
          // Find the raw item to get current date values
          const rawItem = rawItems.find(item => item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID);
          
          if (rawItem && rawItem.column_values) {
            const columnValues = rawItem.column_values;
            const today = new Date();
            
            // Prepare all date updates to run in parallel
            const dateUpdatePromises = [
              // 1. Set Last Reading Date to today
              updateDateColumn({
                boardId: taskBoardId,
                itemId: selectedTask.id,
                columnId: METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
                date: today,
              }),
            ];

            // 2. Push Window Start (date4) by 1 month
            const currentWindowStartText = parseColumnValue(columnValues, taskConfig.startDateColumnId);
            if (currentWindowStartText) {
              const windowStart = normalizeDateOnly(currentWindowStartText);
              if (windowStart) {
                const newWindowStart = new Date(windowStart);
                newWindowStart.setMonth(newWindowStart.getMonth() + 1);
                dateUpdatePromises.push(
                  updateDateColumn({
                    boardId: taskBoardId,
                    itemId: selectedTask.id,
                    columnId: taskConfig.startDateColumnId,
                    date: newWindowStart,
                  })
                );
              }
            }

            // 3. Push Window End by 1 month
            const currentWindowEndText = parseColumnValue(columnValues, taskConfig.endDateColumnId);
            if (currentWindowEndText) {
              const windowEnd = normalizeDateOnly(currentWindowEndText);
              if (windowEnd) {
                const newWindowEnd = new Date(windowEnd);
                newWindowEnd.setMonth(newWindowEnd.getMonth() + 1);
                dateUpdatePromises.push(
                  updateDateColumn({
                    boardId: taskBoardId,
                    itemId: selectedTask.id,
                    columnId: taskConfig.endDateColumnId,
                    date: newWindowEnd,
                  })
                );
              }
            }

            // 4. Clear Due Date (date_mky9dgw1)
            dateUpdatePromises.push(
              clearDateColumn({
                boardId: taskBoardId,
                itemId: selectedTask.id,
                columnId: taskConfig.dueDateColumnId,
              })
            );

            // Check if readings have already been saved individually (to prevent duplicate uploads)
            const todayNormalized = normalizeDateOnly(today);
            const electricityAlreadySaved = readingDates?.electricity && 
              normalizeDateOnly(readingDates.electricity).getTime() === todayNormalized.getTime();
            const waterAlreadySaved = readingDates?.water && 
              normalizeDateOnly(readingDates.water).getTime() === todayNormalized.getTime();
            const gasAlreadySaved = readingDates?.gas && 
              normalizeDateOnly(readingDates.gas).getTime() === todayNormalized.getTime();

            // OPTIMIZATION: Run electricity update in parallel with date updates (no dependency)
            // Only update if not already saved individually
            const electricityColumnId = ELECTRICITY_COLUMN_ID;
            const electricityUpdatePromises = [];
            
            if (electricityReading.trim() !== "" && !electricityAlreadySaved) {
              electricityUpdatePromises.push(
                updateTextColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: electricityColumnId,
                  value: electricityReading.trim(),
                }).catch(err => {
                  console.error("Error updating electricity reading:", err);
                  return null; // Continue even if fails
                })
              );
              
              // Set electricity reading date if not already saved
              electricityUpdatePromises.push(
                updateDateColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: ELECTRICITY_DATE_COLUMN_ID,
                  date: today,
                }).catch(err => {
                  console.error("Error updating electricity reading date:", err);
                  return null;
                })
              );
            }
            
            const electricityUpdatePromise = electricityUpdatePromises.length > 0
              ? Promise.allSettled(electricityUpdatePromises)
              : Promise.resolve(null);

            // OPTIMIZATION: Start water/gas clear operations in parallel (they don't depend on electricity or dates)
            // Only clear/upload if not already saved individually
            const waterColumnId = WATER_COLUMN_ID;
            const gasColumnId = GAS_COLUMN_ID;
            const clearPromises = [];
            
            if (waterReading.length > 0 && !waterAlreadySaved) {
              clearPromises.push(
                clearFileColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: waterColumnId,
                }).catch(err => {
                  console.error("Error clearing water meter photos:", err);
                  return null;
                })
              );
            }
            
            if (gasReading.length > 0 && !gasAlreadySaved) {
              clearPromises.push(
                clearFileColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: gasColumnId,
                }).catch(err => {
                  console.error("Error clearing gas meter photos:", err);
                  return null;
                })
              );
            }

            // Execute date updates, electricity update, and clear operations in parallel
            await Promise.allSettled([
              ...dateUpdatePromises,
              electricityUpdatePromise,
              ...clearPromises,
            ]);

            // Set status back to "In progress" (can run in parallel with file uploads)
            const statusUpdatePromise = updateStatusColumn({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              columnId: taskConfig.statusColumnId,
              label: "In progress",
            });

            // Update rawItems to reflect all changes (status, Last Reading Date, cleared due date, updated window dates)
            const todayDateStr = today.toISOString().split('T')[0];
            
            // Calculate new window dates (reuse variables already declared above)
            let newWindowStartStr = null;
            let newWindowEndStr = null;
            
            if (currentWindowStartText) {
              const windowStart = normalizeDateOnly(currentWindowStartText);
              if (windowStart) {
                const newWindowStart = new Date(windowStart);
                newWindowStart.setMonth(newWindowStart.getMonth() + 1);
                newWindowStartStr = newWindowStart.toISOString().split('T')[0];
              }
            }
            
            if (currentWindowEndText) {
              const windowEnd = normalizeDateOnly(currentWindowEndText);
              if (windowEnd) {
                const newWindowEnd = new Date(windowEnd);
                newWindowEnd.setMonth(newWindowEnd.getMonth() + 1);
                newWindowEndStr = newWindowEnd.toISOString().split('T')[0];
              }
            }

            const updatedRawItems = rawItems.map(item => {
              if (item.id === selectedTask.id && item._boardId === taskBoardId) {
                const updatedItem = { ...item };
                const updatedColumnValues = [...(item.column_values || [])];
                
                // Update status column
                const statusColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.statusColumnId);
                if (statusColumnIndex >= 0) {
                  updatedColumnValues[statusColumnIndex] = {
                    ...updatedColumnValues[statusColumnIndex],
                    text: "In progress",
                    value: JSON.stringify({ label: "In progress", index: 0 }),
                  };
                }
                
                // Update Last Reading Date column
                const lastReadingDateColumnIndex = updatedColumnValues.findIndex(col => col.id === METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID);
                if (lastReadingDateColumnIndex >= 0) {
                  updatedColumnValues[lastReadingDateColumnIndex] = {
                    ...updatedColumnValues[lastReadingDateColumnIndex],
                    text: todayDateStr,
                    value: JSON.stringify({ date: todayDateStr }),
                  };
                } else {
                  // Add Last Reading Date column if it doesn't exist
                  updatedColumnValues.push({
                    id: METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
                    type: "date",
                    text: todayDateStr,
                    value: JSON.stringify({ date: todayDateStr }),
                  });
                }
                
                // Clear due date column
                if (taskConfig.dueDateColumnId) {
                  const dueDateColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.dueDateColumnId);
                  if (dueDateColumnIndex >= 0) {
                    updatedColumnValues[dueDateColumnIndex] = {
                      ...updatedColumnValues[dueDateColumnIndex],
                      text: "",
                      value: null,
                    };
                  }
                }
                
                // Update Window Start date if calculated
                if (newWindowStartStr && taskConfig.startDateColumnId) {
                  const windowStartColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.startDateColumnId);
                  if (windowStartColumnIndex >= 0) {
                    updatedColumnValues[windowStartColumnIndex] = {
                      ...updatedColumnValues[windowStartColumnIndex],
                      text: newWindowStartStr,
                      value: JSON.stringify({ date: newWindowStartStr }),
                    };
                  }
                }
                
                // Update Window End date if calculated
                if (newWindowEndStr && taskConfig.endDateColumnId) {
                  const windowEndColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.endDateColumnId);
                  if (windowEndColumnIndex >= 0) {
                    updatedColumnValues[windowEndColumnIndex] = {
                      ...updatedColumnValues[windowEndColumnIndex],
                      text: newWindowEndStr,
                      value: JSON.stringify({ date: newWindowEndStr }),
                    };
                  }
                }
                
                updatedItem.column_values = updatedColumnValues;
                return updatedItem;
              }
              return item;
            });
            updateRawItems(updatedRawItems);

            // OPTIMIZATION: Upload water and gas images in parallel with status update (clears already done above)
            // Only upload if not already saved individually
            const imageUploadPromises = [];
            const readingDatePromises = [];

            // Upload water images (clear already done above) - only if not already saved
            if (waterReading.length > 0 && !waterAlreadySaved) {
              imageUploadPromises.push(
                (async () => {
                  try {
                    // PHASE 3: Track progress for water image uploads
                    setConfirmationProgress(prev => ({
                      ...prev,
                      uploading: true,
                      total: prev.total + waterReading.length,
                      currentOperation: "Uploading water meter photos...",
                    }));
                    
                    // Upload all water images in parallel
                    const waterUploadPromises = waterReading.map((file, index) =>
                      addFileToColumn({
                        itemId: selectedTask.id,
                        columnId: waterColumnId,
                        file: file,
                      }).then(() => {
                        // Update progress after each upload
                        setConfirmationProgress(prev => ({
                          ...prev,
                          completed: prev.completed + 1,
                        }));
                        return null;
                      }).catch(fileErr => {
                        console.error("Error uploading water meter photo:", file.name, fileErr);
                        // Still count as completed for progress
                        setConfirmationProgress(prev => ({
                          ...prev,
                          completed: prev.completed + 1,
                        }));
                        return null; // Continue even if one fails
                      })
                    );

                    await Promise.allSettled(waterUploadPromises);
                  } catch (uploadErr) {
                    console.error("Error uploading water meter photos:", uploadErr);
                  }
                })()
              );
              
              // Set water reading date
              readingDatePromises.push(
                updateDateColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: WATER_DATE_COLUMN_ID,
                  date: today,
                }).catch(err => {
                  console.error("Error updating water reading date:", err);
                  return null;
                })
              );
            }

            // Upload gas images (clear already done above) - only if not already saved
            if (gasReading.length > 0 && !gasAlreadySaved) {
              imageUploadPromises.push(
                (async () => {
                  try {
                    // PHASE 3: Track progress for gas image uploads
                    setConfirmationProgress(prev => ({
                      ...prev,
                      uploading: true,
                      total: prev.total + gasReading.length,
                      currentOperation: "Uploading gas meter photos...",
                    }));
                    
                    // Upload all gas images in parallel
                    const gasUploadPromises = gasReading.map((file, index) =>
                      addFileToColumn({
                        itemId: selectedTask.id,
                        columnId: gasColumnId,
                        file: file,
                      }).then(() => {
                        // Update progress after each upload
                        setConfirmationProgress(prev => ({
                          ...prev,
                          completed: prev.completed + 1,
                        }));
                        return null;
                      }).catch(fileErr => {
                        console.error("Error uploading gas meter photo:", file.name, fileErr);
                        // Still count as completed for progress
                        setConfirmationProgress(prev => ({
                          ...prev,
                          completed: prev.completed + 1,
                        }));
                        return null; // Continue even if one fails
                      })
                    );

                    await Promise.allSettled(gasUploadPromises);
                  } catch (uploadErr) {
                    console.error("Error uploading gas meter photos:", uploadErr);
                  }
                })()
              );
              
              // Set gas reading date
              readingDatePromises.push(
                updateDateColumn({
                  boardId: taskBoardId,
                  itemId: selectedTask.id,
                  columnId: GAS_DATE_COLUMN_ID,
                  date: today,
                }).catch(err => {
                  console.error("Error updating gas reading date:", err);
                  return null;
                })
              );
            }

            // Execute water and gas image uploads, reading date updates, and status update in parallel
            const allPromises = [
              statusUpdatePromise, // Status update
              ...imageUploadPromises, // Image uploads (if any)
              ...readingDatePromises, // Reading date updates (if any)
            ];
            
            await Promise.allSettled(allPromises);
          }
        } catch (err) {
          console.error("Error executing Meter Readings automation:", err);
          // Don't fail the whole confirmation if automation fails
        }
      }

      // If this is a Cleaning Checks task, set the appropriate status column to Done
      if (taskBoardId === CLEANING_CHECKS_BOARD_ID) {
        try {
          // Check if this is a Check-in or Check-out task
          const isCheckIn = selectedTask.taskType === "Check-in";
          const isCheckOut = selectedTask.taskType === "Check-out";
          
          if (isCheckIn) {
            // Set Check-in Check (status column) to Done
            await updateStatus({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              statusColumnId: taskConfig.checkInStatusColumnId,
              done: true,
            });

            // Update rawItems to reflect the status change
            const updatedRawItems = rawItems.map(item => {
              if (item.id === selectedTask.id && item._boardId === taskBoardId) {
                const updatedItem = { ...item };
                const updatedColumnValues = [...(item.column_values || [])];
                const statusColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.checkInStatusColumnId);
                
                if (statusColumnIndex >= 0) {
                  updatedColumnValues[statusColumnIndex] = {
                    ...updatedColumnValues[statusColumnIndex],
                    text: "Done",
                    value: JSON.stringify({ label: "Done", index: 0 }),
                  };
                }
                
                updatedItem.column_values = updatedColumnValues;
                return updatedItem;
              }
              return item;
            });
            updateRawItems(updatedRawItems);
          } else if (isCheckOut) {
            // Set Check-out Check (color_mkyh2380) to Done
            await updateStatusColumn({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              columnId: taskConfig.checkOutStatusColumnId,
              label: "Done",
            });

            // Update rawItems to reflect the status change
            const updatedRawItems = rawItems.map(item => {
              if (item.id === selectedTask.id && item._boardId === taskBoardId) {
                const updatedItem = { ...item };
                const updatedColumnValues = [...(item.column_values || [])];
                const statusColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.checkOutStatusColumnId);
                
                if (statusColumnIndex >= 0) {
                  updatedColumnValues[statusColumnIndex] = {
                    ...updatedColumnValues[statusColumnIndex],
                    text: "Done",
                    value: JSON.stringify({ label: "Done", index: 0 }),
                  };
                }
                
                updatedItem.column_values = updatedColumnValues;
                return updatedItem;
              }
              return item;
            });
            updateRawItems(updatedRawItems);
          }
        } catch (err) {
          console.error("Error executing Cleaning Checks automation:", err);
          // Don't fail the whole confirmation if automation fails
        }
      }

      // If this is a Weekly Checks task, execute the automation logic
      if (taskBoardId === WEEKLY_CHECKS_BOARD_ID) {
        try {
          // Find the raw item to get current date values and type
          const rawItem = rawItems.find(item => item.id === selectedTask.id && item._boardId === WEEKLY_CHECKS_BOARD_ID);
          
          if (rawItem && rawItem.column_values) {
            const columnValues = rawItem.column_values;
            
            // Determine task type from status column (Weekly Check or Monthly Check)
            const typeText = parseColumnValue(columnValues, taskConfig.typeColumnId);
            const normalizedType = typeText ? typeText.toLowerCase().trim() : "";
            const isMonthlyCheck = normalizedType === "monthly check";
            const isWeeklyCheck = normalizedType === "weekly check";
            
            // Prepare date updates to run in parallel
            const today = new Date();
            const dateUpdatePromises = [
              // 1. Set Last Completed Date to today
              updateDateColumn({
                boardId: taskBoardId,
                itemId: selectedTask.id,
                columnId: LAST_COMPLETED_DATE_COLUMN_ID,
                date: today,
              }),
            ];

            // 3. Get current Date and move it forward
            const currentDateText = parseColumnValue(columnValues, taskConfig.dueDateColumnId);
            let taskCurrentDate = null;
            if (currentDateText) {
              taskCurrentDate = normalizeDateOnly(currentDateText);
              if (taskCurrentDate) {
                const newDate = new Date(taskCurrentDate);
                // Move forward 7 days for Weekly Check, 14 days (2 weeks) for Monthly Check
                const daysToAdd = isMonthlyCheck ? 14 : (isWeeklyCheck ? 7 : 7); // Default to 7 if type not recognized
                newDate.setDate(newDate.getDate() + daysToAdd);
                
                dateUpdatePromises.push(
                  updateDateColumn({
                    boardId: taskBoardId,
                    itemId: selectedTask.id,
                    columnId: taskConfig.dueDateColumnId,
                    date: newDate,
                  })
                );
              }
            }

            // Execute all date updates in parallel
            await Promise.all(dateUpdatePromises);

            // 4. Set status back to "In Progress" after dates have been updated
            await updateStatusColumn({
              boardId: taskBoardId,
              itemId: selectedTask.id,
              columnId: taskConfig.statusColumnId,
              label: "In Progress",
            });

            // Update rawItems to reflect all changes (status, Last Completed Date, and new due date)
            const todayDateStr = today.toISOString().split('T')[0];
            const newDateStr = taskCurrentDate ? (() => {
              const newDate = new Date(taskCurrentDate);
              const daysToAdd = isMonthlyCheck ? 14 : (isWeeklyCheck ? 7 : 7);
              newDate.setDate(newDate.getDate() + daysToAdd);
              return newDate.toISOString().split('T')[0];
            })() : null;

            const updatedRawItems = rawItems.map(item => {
              if (item.id === selectedTask.id && item._boardId === taskBoardId) {
                const updatedItem = { ...item };
                const updatedColumnValues = [...(item.column_values || [])];
                
                // Update status column
                const statusColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.statusColumnId);
                if (statusColumnIndex >= 0) {
                  updatedColumnValues[statusColumnIndex] = {
                    ...updatedColumnValues[statusColumnIndex],
                    text: "In Progress",
                    value: JSON.stringify({ label: "In Progress", index: 0 }),
                  };
                }
                
                // Update Last Completed Date column
                const lastCompletedDateColumnIndex = updatedColumnValues.findIndex(col => col.id === LAST_COMPLETED_DATE_COLUMN_ID);
                if (lastCompletedDateColumnIndex >= 0) {
                  updatedColumnValues[lastCompletedDateColumnIndex] = {
                    ...updatedColumnValues[lastCompletedDateColumnIndex],
                    text: todayDateStr,
                    value: JSON.stringify({ date: todayDateStr }),
                  };
                } else {
                  // Add Last Completed Date column if it doesn't exist
                  updatedColumnValues.push({
                    id: LAST_COMPLETED_DATE_COLUMN_ID,
                    type: "date",
                    text: todayDateStr,
                    value: JSON.stringify({ date: todayDateStr }),
                  });
                }
                
                // Update due date column if new date was calculated
                if (newDateStr && taskConfig.dueDateColumnId) {
                  const dueDateColumnIndex = updatedColumnValues.findIndex(col => col.id === taskConfig.dueDateColumnId);
                  if (dueDateColumnIndex >= 0) {
                    updatedColumnValues[dueDateColumnIndex] = {
                      ...updatedColumnValues[dueDateColumnIndex],
                      text: newDateStr,
                      value: JSON.stringify({ date: newDateStr }),
                    };
                  }
                }
                
                updatedItem.column_values = updatedColumnValues;
                return updatedItem;
              }
              return item;
            });
            updateRawItems(updatedRawItems);
            // Note: tasksByProperty update happens at the end for all task types
          }
        } catch (err) {
          console.error("Error executing Weekly Checks automation:", err);
          // Don't fail the whole confirmation if automation fails
        }
      }

      // If this is a Weekly Checks task, update Meter Readings tasks for the same property
      if (taskBoardId === WEEKLY_CHECKS_BOARD_ID) {
        try {
          // Get the updated Weekly Check item (after automation ran) to get the NEW due date and type
          const updatedWeeklyCheckItem = rawItems.find(item => item.id === selectedTask.id && item._boardId === WEEKLY_CHECKS_BOARD_ID);
          
          if (!updatedWeeklyCheckItem || !updatedWeeklyCheckItem.column_values) {
            // Fallback: use selectedTask date if rawItem not found
            console.warn("Could not find updated Weekly Check item, using selectedTask date");
          }

          // Get the NEW due date from the updated item (after automation moved it forward)
          const newDueDateText = updatedWeeklyCheckItem?.column_values 
            ? parseColumnValue(updatedWeeklyCheckItem.column_values, taskConfig.dueDateColumnId)
            : null;
          
          // Get the type to determine how many days to add for next check
          const typeText = updatedWeeklyCheckItem?.column_values
            ? parseColumnValue(updatedWeeklyCheckItem.column_values, taskConfig.typeColumnId)
            : null;
          const normalizedType = typeText ? typeText.toLowerCase().trim() : "";
          const isMonthlyCheck = normalizedType === "monthly check";
          const isWeeklyCheck = normalizedType === "weekly check";
          
          // Calculate next Weekly Checks date based on NEW due date and type
          let nextWeeklyChecksDate = null;
          if (newDueDateText) {
            const newDueDate = normalizeDateOnly(newDueDateText);
            if (newDueDate) {
              // Next check is: new due date + interval (7 days for weekly, 14 days for monthly)
              const daysToAdd = isMonthlyCheck ? 14 : (isWeeklyCheck ? 7 : 7); // Default to 7 if type not recognized
              nextWeeklyChecksDate = new Date(newDueDate);
              nextWeeklyChecksDate.setDate(nextWeeklyChecksDate.getDate() + daysToAdd);
            }
          }
          
          // Fallback: if we couldn't get the new date, calculate from selectedTask date
          // Automation already moved the due date forward by daysToAdd, so next check = old date + 2*daysToAdd
          if (!nextWeeklyChecksDate) {
            const currentDueDate = selectedTask.date ? new Date(selectedTask.date) : new Date();
            const daysToAdd = isMonthlyCheck ? 14 : (isWeeklyCheck ? 7 : 7);
            nextWeeklyChecksDate = new Date(currentDueDate);
            // Add days twice: once for what automation did, once for the next check
            nextWeeklyChecksDate.setDate(nextWeeklyChecksDate.getDate() + daysToAdd + daysToAdd);
          }

          // Get property item IDs from the completed Weekly Checks task
          const propertyItemIds = selectedTask.propertyItemIds || [];
          
          if (propertyItemIds.length > 0 && nextWeeklyChecksDate) {
            // Use cached raw items from context instead of making a new API call
            const meterReadingsBoardId = METER_READINGS_BOARD_ID;
            const meterReadingsConfig = BOARD_CONFIG[meterReadingsBoardId];

            // Filter cached raw items to get Meter Readings tasks for the same property
            const meterReadingsItems = rawItems.filter(item => {
              // Only Meter Readings board items
              if (item._boardId !== meterReadingsBoardId) return false;
              
              // Check if linked to any of the properties
              const linkedItems = item.linked_items || [];
              const linkedPropertyIds = linkedItems.map(li => li.id).filter(Boolean);
              
              return propertyItemIds.some(propId => 
                linkedPropertyIds.includes(String(propId)) || linkedPropertyIds.includes(Number(propId))
              );
            });

            // Process Meter Readings tasks that match the property and are not done
            for (const mrItem of meterReadingsItems) {

              // Check if task is already done
              const columnValues = mrItem.column_values || [];
              const statusText = parseColumnValue(columnValues, meterReadingsConfig.statusColumnId);
              if (isTaskDone(statusText, DONE_LABELS)) continue;

              // Get date window - start from date4 (Window Start), end from date_mky3yhva (Window End)
              const windowStartText = parseColumnValue(columnValues, meterReadingsConfig.startDateColumnId); // date4
              const endDateText = parseColumnValue(columnValues, meterReadingsConfig.endDateColumnId);

              if (!windowStartText || !endDateText) continue;

              const windowStart = normalizeDateOnly(windowStartText);
              const windowEnd = normalizeDateOnly(endDateText);
              const nextDate = normalizeDateOnly(nextWeeklyChecksDate);

              if (!windowStart || !windowEnd || !nextDate) continue;

              // Determine which date to set for Meter Reading due date
              let dateToSet = null;
              
              if (nextDate.getTime() >= windowStart.getTime() && nextDate.getTime() <= windowEnd.getTime()) {
                // Next Weekly Checks date is within window - use that date
                dateToSet = nextDate;
              } else if (nextDate.getTime() > windowEnd.getTime()) {
                // Next Weekly Checks date is AFTER window end - use window start date
                dateToSet = windowStart;
              } else {
                // Next Weekly Checks date is BEFORE window start - don't update (keep current due date)
                // Skip this Meter Reading task
                continue;
              }

              // Check if the Due Date column is empty before updating
              // Only update if the date has not been manually set
              const currentDueDateText = parseColumnValue(columnValues, meterReadingsConfig.dueDateColumnId);
              const isDueDateEmpty = !currentDueDateText || currentDueDateText.trim() === "" || currentDueDateText.toLowerCase() === "no date";

              // Update the Meter Readings due date only if it's empty
              if (dateToSet && isDueDateEmpty) {
                try {
                  await updateDateColumn({
                    boardId: meterReadingsBoardId,
                    itemId: mrItem.id,
                    columnId: meterReadingsConfig.dueDateColumnId, // date_mky9dgw1
                    date: dateToSet,
                  });
                } catch (err) {
                  console.error(`Error updating Meter Readings task ${mrItem.id} date:`, err);
                }
              } else if (dateToSet && !isDueDateEmpty) {
                // Skip the update because date was manually set
              }
            }
          }
        } catch (err) {
          console.error("Error updating Meter Readings tasks after Weekly Checks completion:", err);
          // Don't fail the whole operation if this fails
        }
      }

      // Note: Meter Readings meter reading columns (electricity, water, gas) are now handled
      // in the automation block above for better performance (parallel execution)

      // Update tasksByProperty to show the task as completed entry for all task types
      // This ensures tasks remain visible after confirmation in both daily and weekly views
      const updated = { ...tasksByProperty };
      const today = new Date();
      const todayNormalized = normalizeDateOnly(today);
      const todayDateStr = today.toISOString().split('T')[0];
      const propertyName = selectedTask.propertyName;
      
      if (viewType === "daily") {
        // Daily view: Update task to show as completed entry for today
        // Find the task in its current location (it might be under the property name)
        let taskFound = false;
        let taskData = null;
        
        if (updated[propertyName]) {
          const taskIndex = updated[propertyName].findIndex(t => t.id === selectedTask.id);
          if (taskIndex >= 0) {
            taskData = updated[propertyName][taskIndex];
            taskFound = true;
          }
        }
        
        if (taskFound && taskData) {
          // Update task in place to show as completed
          updated[propertyName] = updated[propertyName].map((t) => {
            if (t.id === selectedTask.id) {
              return {
                ...t,
                done: true,
                isCompletedEntry: true,
                date: todayNormalized || t.date,
                lastCompletedDate: todayNormalized,
                lastCompletedDateText: todayDateStr,
              };
            }
            return t;
          });
        } else {
          // Task not found in current location - add it as a completed entry
          // This can happen if the task's due date was cleared and it needs to show as completed
          if (!updated[propertyName]) {
            updated[propertyName] = [];
          }
          const completedTask = {
            ...selectedTask,
            done: true,
            isCompletedEntry: true,
            date: todayNormalized || selectedTask.date,
            lastCompletedDate: todayNormalized,
            lastCompletedDateText: todayDateStr,
          };
          updated[propertyName].push(completedTask);
        }
      } else {
        // Weekly view: Update task to show as completed entry for today
        const todayDayKey = todayNormalized ? getDayAbbreviation(todayNormalized) : null;
        const weekStartNormalized = normalizeDateOnly(currentWeekStart);
        const weekEnd = new Date(weekStartNormalized);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekEndNormalized = normalizeDateOnly(weekEnd);
        const isTodayInWeek = todayNormalized && weekStartNormalized && weekEndNormalized &&
          todayNormalized.getTime() >= weekStartNormalized.getTime() &&
          todayNormalized.getTime() <= weekEndNormalized.getTime();
        
        // Find the task in its current location
        const dayKeys = Object.keys(updated);
        let taskFound = false;
        let taskData = null;
        let foundDayKey = null;
        
        for (const dayKey of dayKeys) {
          if (updated[dayKey] && typeof updated[dayKey] === "object" && updated[dayKey][propertyName]) {
            const taskIndex = updated[dayKey][propertyName].findIndex(t => t.id === selectedTask.id);
            if (taskIndex >= 0) {
              taskData = updated[dayKey][propertyName][taskIndex];
              foundDayKey = dayKey;
              taskFound = true;
              break;
            }
          }
        }
        
        if (taskFound && taskData) {
          // Create completed task entry
          const completedTask = {
            ...taskData,
            done: true,
            isCompletedEntry: true,
            date: todayNormalized || taskData.date,
            lastCompletedDate: todayNormalized,
            lastCompletedDateText: todayDateStr,
          };
          
          // If today is in the current week, move task to today's day
          if (isTodayInWeek && todayDayKey && foundDayKey !== todayDayKey) {
            // Remove from old location
            updated[foundDayKey] = {
              ...updated[foundDayKey],
              [propertyName]: updated[foundDayKey][propertyName].filter(t => t.id !== selectedTask.id),
            };
            
            // Add to today's day
            if (!updated[todayDayKey]) {
              updated[todayDayKey] = {};
            }
            if (!updated[todayDayKey][propertyName]) {
              updated[todayDayKey][propertyName] = [];
            }
            updated[todayDayKey][propertyName].push(completedTask);
          } else {
            // Update in place
            updated[foundDayKey] = {
              ...updated[foundDayKey],
              [propertyName]: updated[foundDayKey][propertyName].map((t) => {
                if (t.id === selectedTask.id) {
                  return completedTask;
                }
                return t;
              }),
            };
          }
        }
      }
      
      updateTasksByProperty(updated);

      // Popup already closed after optimistic update for instant feedback
      // All API calls completed successfully
      // PHASE 3: Reset progress tracking (only if component is still mounted)
      if (isMountedRef.current) {
        setConfirmationProgress({
          uploading: false,
          total: 0,
          completed: 0,
          currentOperation: null,
        });
      }
    } catch (err) {
      console.error("Error confirming task:", err);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        // Revert optimistic update on error
        updateTasksByProperty(originalState);
        // Show error toast (popup is already closed, so toast is the only feedback)
        if (toast) toast.error("Failed to confirm task. Please try again.");
        // PHASE 3: Reset progress tracking on error
        setConfirmationProgress({
          uploading: false,
          total: 0,
          completed: 0,
          currentOperation: null,
        });
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setConfirmingTask(false);
        setShowConfirmedState(false);
      }
    }
  }, [selectedTask, confirmingTask, boardId, config, viewType, tasksByProperty, updateTasksByProperty, waterReading, gasReading, electricityReading, taskImages, taskComments, timeSpent, handleClosePopup, rawItems, handleRefresh, currentDate, currentWeekStart, updateRawItems, savingReading, readingDates, toast]);

  // Handler to save electricity reading with date
  const handleSaveElectricityReading = useCallback(async () => {
    if (!selectedTask || selectedTask.boardId !== METER_READINGS_BOARD_ID || !electricityReading.trim()) {
      if (toast) toast.warning("Please enter an electricity reading");
      return;
    }

    setSavingReading(prev => ({ ...prev, electricity: true }));

    try {
      const today = new Date();
      const todayNormalized = normalizeDateOnly(today);
      const todayDateStr = today.toISOString().split('T')[0];
      
      // OPTIMIZATION: Update UI optimistically before API calls complete
      setReadingDates(prev => ({ ...prev, electricity: todayNormalized }));
      setCompletedMeterReadingData(prev => ({ ...prev, electricity: electricityReading.trim() }));
      
      // OPTIMIZATION: Update rawItems optimistically
      const optimisticRawItems = rawItems.map(item => {
        if (item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID) {
          const updatedItem = { ...item };
          const updatedColumnValues = [...(item.column_values || [])];
          
          // Update electricity column
          const electricityColIndex = updatedColumnValues.findIndex(col => col.id === ELECTRICITY_COLUMN_ID);
          if (electricityColIndex >= 0) {
            updatedColumnValues[electricityColIndex] = {
              ...updatedColumnValues[electricityColIndex],
              text: electricityReading.trim(),
              value: electricityReading.trim(),
            };
          } else {
            updatedColumnValues.push({
              id: ELECTRICITY_COLUMN_ID,
              type: "text",
              text: electricityReading.trim(),
              value: electricityReading.trim(),
            });
          }
          
          // Update electricity date column
          const electricityDateColIndex = updatedColumnValues.findIndex(col => col.id === ELECTRICITY_DATE_COLUMN_ID);
          if (electricityDateColIndex >= 0) {
            updatedColumnValues[electricityDateColIndex] = {
              ...updatedColumnValues[electricityDateColIndex],
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            };
          } else {
            updatedColumnValues.push({
              id: ELECTRICITY_DATE_COLUMN_ID,
              type: "date",
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            });
          }
          
          updatedItem.column_values = updatedColumnValues;
          return updatedItem;
        }
        return item;
      });
      updateRawItems(optimisticRawItems);
      
      // OPTIMIZATION: Run text and date updates in parallel (no dependency)
      await Promise.allSettled([
        updateTextColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: selectedTask.id,
          columnId: ELECTRICITY_COLUMN_ID,
          value: electricityReading.trim(),
        }),
        updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: selectedTask.id,
          columnId: ELECTRICITY_DATE_COLUMN_ID,
          date: today,
        }),
      ]);

      // OPTIMIZATION: Skip getItemById - we already updated optimistically
      // The optimistic update is sufficient, and we know the values we set
      if (toast) toast.success("Electricity reading saved!");
    } catch (err) {
      console.error("Error saving electricity reading:", err);
      // Revert optimistic update on error
      const originalItem = rawItems.find(item => item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID);
      if (originalItem) {
        const revertedRawItems = rawItems.map(item => {
          if (item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID) {
            return originalItem;
          }
          return item;
        });
        updateRawItems(revertedRawItems);
      }
      setReadingDates(prev => ({ ...prev, electricity: null }));
      if (toast) toast.error("Failed to save electricity reading. Please try again.");
    } finally {
      setSavingReading(prev => ({ ...prev, electricity: false }));
    }
  }, [selectedTask, electricityReading, rawItems, updateRawItems, toast]);

  // Handler to save water reading with date
  const handleSaveWaterReading = useCallback(async () => {
    if (!selectedTask || selectedTask.boardId !== METER_READINGS_BOARD_ID || waterReading.length === 0) {
      if (toast) toast.warning("Please upload at least one water meter photo");
      return;
    }

    setSavingReading(prev => ({ ...prev, water: true }));

    try {
      const today = new Date();
      const todayNormalized = normalizeDateOnly(today);
      const todayDateStr = today.toISOString().split('T')[0];
      
      // Don't set readingDates optimistically for image uploads - wait until uploads complete
      // This prevents showing checkmark while images are still uploading
      
      // Clear existing water photos (must complete before uploads)
      await clearFileColumn({
        boardId: METER_READINGS_BOARD_ID,
        itemId: selectedTask.id,
        columnId: WATER_COLUMN_ID,
      });

      // OPTIMIZATION: Upload images and update date in parallel (no dependency between them)
      await Promise.allSettled([
        // Upload all water images in parallel
        Promise.allSettled(
          waterReading.map(file =>
            addFileToColumn({
              itemId: selectedTask.id,
              columnId: WATER_COLUMN_ID,
              file: file,
            }).catch(fileErr => {
              console.error("Error uploading water meter photo:", file.name, fileErr);
              return null;
            })
          )
        ),
        // Update date in parallel with uploads
        updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: selectedTask.id,
          columnId: WATER_DATE_COLUMN_ID,
          date: today,
        }),
      ]);

      // Now that uploads are complete, update UI state and rawItems
      setReadingDates(prev => ({ ...prev, water: todayNormalized }));
      
      // Update rawItems with the date (images are already uploaded)
      const updatedRawItems = rawItems.map(item => {
        if (item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID) {
          const updatedItem = { ...item };
          const updatedColumnValues = [...(item.column_values || [])];
          
          // Update water date column
          const waterDateColIndex = updatedColumnValues.findIndex(col => col.id === WATER_DATE_COLUMN_ID);
          if (waterDateColIndex >= 0) {
            updatedColumnValues[waterDateColIndex] = {
              ...updatedColumnValues[waterDateColIndex],
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            };
          } else {
            updatedColumnValues.push({
              id: WATER_DATE_COLUMN_ID,
              type: "date",
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            });
          }
          
          updatedItem.column_values = updatedColumnValues;
          return updatedItem;
        }
        return item;
      });
      updateRawItems(updatedRawItems);

      // OPTIMIZATION: Skip getItemById - we already updated the state
      if (toast) toast.success("Water reading saved!");
    } catch (err) {
      console.error("Error saving water reading:", err);
      // No need to revert readingDates since we didn't set it optimistically
      // rawItems also wasn't updated optimistically, so no revert needed
      if (toast) toast.error("Failed to save water reading. Please try again.");
    } finally {
      setSavingReading(prev => ({ ...prev, water: false }));
    }
  }, [selectedTask, waterReading, rawItems, updateRawItems, toast]);

  // Handler to save gas reading with date
  const handleSaveGasReading = useCallback(async () => {
    if (!selectedTask || selectedTask.boardId !== METER_READINGS_BOARD_ID || gasReading.length === 0) {
      if (toast) toast.warning("Please upload at least one gas meter photo");
      return;
    }

    setSavingReading(prev => ({ ...prev, gas: true }));

    try {
      const today = new Date();
      const todayNormalized = normalizeDateOnly(today);
      const todayDateStr = today.toISOString().split('T')[0];
      
      // Don't set readingDates optimistically for image uploads - wait until uploads complete
      // This prevents showing checkmark while images are still uploading
      
      // Clear existing gas photos (must complete before uploads)
      await clearFileColumn({
        boardId: METER_READINGS_BOARD_ID,
        itemId: selectedTask.id,
        columnId: GAS_COLUMN_ID,
      });

      // OPTIMIZATION: Upload images and update date in parallel (no dependency between them)
      await Promise.allSettled([
        // Upload all gas images in parallel
        Promise.allSettled(
          gasReading.map(file =>
            addFileToColumn({
              itemId: selectedTask.id,
              columnId: GAS_COLUMN_ID,
              file: file,
            }).catch(fileErr => {
              console.error("Error uploading gas meter photo:", file.name, fileErr);
              return null;
            })
          )
        ),
        // Update date in parallel with uploads
        updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: selectedTask.id,
          columnId: GAS_DATE_COLUMN_ID,
          date: today,
        }),
      ]);

      // Now that uploads are complete, update UI state and rawItems
      setReadingDates(prev => ({ ...prev, gas: todayNormalized }));
      
      // Update rawItems with the date (images are already uploaded)
      const updatedRawItems = rawItems.map(item => {
        if (item.id === selectedTask.id && item._boardId === METER_READINGS_BOARD_ID) {
          const updatedItem = { ...item };
          const updatedColumnValues = [...(item.column_values || [])];
          
          // Update gas date column
          const gasDateColIndex = updatedColumnValues.findIndex(col => col.id === GAS_DATE_COLUMN_ID);
          if (gasDateColIndex >= 0) {
            updatedColumnValues[gasDateColIndex] = {
              ...updatedColumnValues[gasDateColIndex],
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            };
          } else {
            updatedColumnValues.push({
              id: GAS_DATE_COLUMN_ID,
              type: "date",
              text: todayDateStr,
              value: JSON.stringify({ date: todayDateStr }),
            });
          }
          
          updatedItem.column_values = updatedColumnValues;
          return updatedItem;
        }
        return item;
      });
      updateRawItems(updatedRawItems);

      // OPTIMIZATION: Skip getItemById - we already updated the state
      if (toast) toast.success("Gas reading saved!");
    } catch (err) {
      console.error("Error saving gas reading:", err);
      // No need to revert readingDates since we didn't set it optimistically
      // rawItems also wasn't updated optimistically, so no revert needed
      if (toast) toast.error("Failed to save gas reading. Please try again.");
    } finally {
      setSavingReading(prev => ({ ...prev, gas: false }));
    }
  }, [selectedTask, gasReading, rawItems, updateRawItems, toast]);

  const handleConfirmTask = useCallback(
    throttle(handleConfirmTaskInternal, 1000),
    [handleConfirmTaskInternal]
  );

  // Throttle send report handler to prevent rapid API calls (max once per 2000ms)
  const handleSendReportInternal = useCallback(async () => {
    if (!reportText.trim() && reportImages.length === 0) {
      if (toast) toast.warning("Please enter a report or upload images");
      return;
    }

    if (!selectedTask) return;

    const reportsBoardId = REPORTS_BOARD_ID;
    const fileColumnId = FILE_COLUMN_ID;
    setSendingReport(true);

    try {
      // Step 1: Create the item in REPORTS board with property already set
      const propertyColumnId = REPORTS_PROPERTY_COLUMN_ID;
      
      // Prepare column values - each value is a JSON string
      const columnValues = {};
      
      // Add property if available
      if (selectedTask.propertyItemIds && selectedTask.propertyItemIds.length > 0) {
        const validPropertyIds = selectedTask.propertyItemIds
          .map(id => {
            const numId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
            return isNaN(numId) ? null : numId;
          })
          .filter(id => id !== null);
        
        if (validPropertyIds.length > 0) {
          // For board relation columns in create_item, use linkedPulseIds format
          columnValues[propertyColumnId] = {
            linkedPulseIds: validPropertyIds.map(id => ({
              linkedPulseId: id
            }))
          };
        }
      }
      
      const createResponse = await createItem({
        boardId: reportsBoardId,
        itemName: reportText.trim() || `Report from ${selectedTask.name}`,
        columnValues: columnValues,
      });

      const newItemId = createResponse?.id;
      
      if (!newItemId) {
        throw new Error("Failed to create report item");
      }

      // Step 3: Upload images if any
      if (reportImages.length > 0) {
        // Monday.com file columns require using add_file_to_column_value mutation
        // We'll upload each file to the column
        for (const file of reportImages) {
          try {
            await addFileToColumn({
              itemId: newItemId,
              columnId: fileColumnId,
              file: file,
            });
          } catch (fileErr) {
            console.error("Error uploading file:", file.name, fileErr);
            // Continue with other files even if one fails
          }
        }
      }

      // Success
      if (toast) toast.success("Report sent successfully!");
      handleClosePopup();
      
      // Refresh tasks if on the reports board
      if (boardId === Number(reportsBoardId) && handleRefresh) {
        handleRefresh();
      }
    } catch (err) {
      console.error("Error sending report:", err);
      if (toast) toast.error("Failed to send report. Please try again.");
    } finally {
      setSendingReport(false);
    }
  }, [reportText, reportImages, selectedTask, boardId, handleRefresh, handleClosePopup]);

  const handleSendReport = useCallback(
    throttle(handleSendReportInternal, 2000),
    [handleSendReportInternal]
  );

  // Throttle create task handler to prevent rapid API calls (max once per 1000ms)
  const handleCreateTaskInternal = useCallback(async () => {
    if (!newTaskTitle.trim()) {
      if (toast) toast.warning("Please enter a task title");
      return;
    }

    if (!newTaskDate) {
      if (toast) toast.warning("Please select a date");
      return;
    }

    if (!newTaskProperty) {
      if (toast) toast.warning("Please select a property");
      return;
    }

    const tasksBoardId = TASKS_BOARD_ID;
    const propertyColumnId = TASKS_PROPERTY_COLUMN_ID;
    const dueDateColumnId = TASKS_DUE_DATE_COLUMN_ID;
    const descriptionColumnId = TASKS_DESCRIPTION_COLUMN_ID_FULL;
    // Use the person column ID specific to the TASKS board, fallback to general personColumnId or default
    const personColumnIdForTask = personColumnIdMap?.[tasksBoardId] || personColumnId || DEFAULT_PERSON_COLUMN_ID;

    setCreatingTask(true);

    try {
      // Prepare column values
      const columnValues = {};

      // Add property (board relation)
      if (newTaskProperty) {
        const propertyId = typeof newTaskProperty === 'string' ? parseInt(newTaskProperty, 10) : Number(newTaskProperty);
        if (!isNaN(propertyId)) {
          columnValues[propertyColumnId] = {
            linkedPulseIds: [{ linkedPulseId: propertyId }]
          };
        }
      }

      // Add date
      if (newTaskDate) {
        const dateStr = newTaskDate instanceof Date 
          ? newTaskDate.toISOString().split('T')[0]
          : typeof newTaskDate === 'string' 
            ? newTaskDate.split('T')[0] 
            : null;
        if (dateStr) {
          columnValues[dueDateColumnId] = { date: dateStr };
        }
      }

      // Add description (long text) - Monday.com expects text for long_text columns
      if (newTaskDescription.trim()) {
        columnValues[descriptionColumnId] = newTaskDescription.trim();
      }

      // Add person (dropdown) - Monday.com expects { labels: [...] } array format for dropdown columns
      if (newTaskPerson && personColumnIdForTask) {
        // For dropdown columns, need to find the label
        // newTaskPerson is the person's ID (which is their name in our structure)
        const person = availablePeople.find(p => p.id === newTaskPerson);
        const personName = person?.name || person?.id || newTaskPerson;
        
        if (personName) {
          // For create_item, dropdown values should be: { labels: ["Person Name"] }
          // The createItem function will stringify the entire columnValues object
          columnValues[personColumnIdForTask] = { labels: [personName] };
        } else {
          console.warn("Person not found in availablePeople:", newTaskPerson, availablePeople);
        }
      }

      // Add Time Tracking (color/status column) - Monday.com expects { label: "Yes" } or { label: "No" }
      if (newTaskTimeTracking) {
        columnValues[TIME_TRACKING_COLUMN_ID] = { label: "Yes" };
      } else {
        columnValues[TIME_TRACKING_COLUMN_ID] = { label: "No" };
      }

      // Create the item
      const createResponse = await createItem({
        boardId: tasksBoardId,
        itemName: newTaskTitle.trim(),
        columnValues: columnValues,
      });

      const newItemId = createResponse?.id;
      
      if (!newItemId) {
        throw new Error("Failed to create task");
      }

      // Optimistically update: Fetch the new item and add it to rawItems instead of full refresh
      try {
        const taskConfig = BOARD_CONFIG[tasksBoardId];
        const boardPersonColumnId = personColumnIdMap?.[tasksBoardId] || personColumnId || DEFAULT_PERSON_COLUMN_ID;
        
        // Build the same column IDs that are normally fetched for TASKS board
        const columnIds = [
          taskConfig.propertyColumnId,
          taskConfig.dueDateColumnId,
          taskConfig.startDateColumnId,
          taskConfig.endDateColumnId,
          taskConfig.statusColumnId,
          boardPersonColumnId,
          TASKS_DESCRIPTION_COLUMN_ID_FULL,
          TIME_TRACKING_COLUMN_ID,
          TASKS_FILE_COLUMN_ID,
          TASKS_COMMENTS_COLUMN_ID,
        ].filter(Boolean); // Remove any undefined/null values

        // Fetch the newly created item with all necessary columns and linked property items
        const newItem = await getItemById({
          boardId: tasksBoardId,
          itemId: newItemId,
          columnIds: columnIds,
          linkToItemColumnId: taskConfig.propertyColumnId, // Fetch linked property items
          linkedBoardId: PROPERTIES_BOARD_ID, // Properties board ID
        });

        if (newItem) {
          // Add board ID and config to the item (same structure as in useTasks.js)
          newItem._boardId = tasksBoardId;
          newItem._config = taskConfig;

          // Add the new item to rawItems
          updateRawItems([...rawItems, newItem]);
        } else {
          // If fetch failed, fall back to full refresh
          console.warn("Failed to fetch new item, falling back to full refresh");
          if (handleRefresh) {
            handleRefresh();
          }
        }
      } catch (fetchErr) {
        // If optimistic update fails, fall back to full refresh
        console.error("Error fetching new item for optimistic update:", fetchErr);
        if (handleRefresh) {
          handleRefresh();
        }
      }

      // Close popup and reset form
      handleCloseNewTaskPopup();
      if (toast) toast.success("Task created successfully!");
    } catch (err) {
      console.error("Error creating task:", err);
      if (toast) toast.error("Failed to create task. Please try again.");
    } finally {
      setCreatingTask(false);
    }
  }, [newTaskTitle, newTaskDate, newTaskProperty, newTaskDescription, newTaskPerson, newTaskTimeTracking, personColumnId, personColumnIdMap, availablePeople, handleRefresh, handleCloseNewTaskPopup, rawItems, updateRawItems]);

  const handleCreateTask = useCallback(
    throttle(handleCreateTaskInternal, 1000),
    [handleCreateTaskInternal]
  );

  // Handle saving notes (extras) for Cleaning Checks tasks
  const handleSaveExtras = useCallback(async () => {
    if (!selectedTask || savingExtras || selectedTask.boardId !== CLEANING_CHECKS_BOARD_ID) return;

    setSavingExtras(true);

    try {
      const taskConfig = BOARD_CONFIG[selectedTask.boardId];
      if (!taskConfig || !taskConfig.extrasColumnId) {
        throw new Error("Notes column not configured");
      }

      await updateTextColumn({
        boardId: selectedTask.boardId,
        itemId: selectedTask.id,
        columnId: taskConfig.extrasColumnId,
        value: extras || "",
      });

      if (toast) toast.success("Notes saved successfully!");
    } catch (err) {
      console.error("Error saving notes:", err);
      if (toast) toast.error("Failed to save notes. Please try again.");
    } finally {
      setSavingExtras(false);
    }
  }, [selectedTask, extras, savingExtras]);

  // Handle delete task for TASKS board
  const handleDeleteTask = useCallback(async () => {
    if (!selectedTask || selectedTask.boardId !== TASKS_BOARD_ID) return;
    if (deletingTask) return;

    if (!window.confirm(`Are you sure you want to delete "${selectedTask.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingTask(true);

    try {
      await deleteItem({ itemId: selectedTask.id });
      if (toast) toast.success("Task deleted successfully!");
      handleClosePopup();
      handleRefresh();
    } catch (err) {
      console.error("Error deleting task:", err);
      if (toast) toast.error("Failed to delete task. Please try again.");
    } finally {
      setDeletingTask(false);
    }
  }, [selectedTask, deletingTask, handleClosePopup, handleRefresh]);

  // Handle save edited task for TASKS board
  const handleSaveTask = useCallback(async () => {
    if (!selectedTask || selectedTask.boardId !== TASKS_BOARD_ID) return;
    if (savingTask) return;

    if (!editTaskName.trim()) {
      if (toast) toast.warning("Task name cannot be empty.");
      return;
    }

    setSavingTask(true);

    try {
      const tasksBoardId = TASKS_BOARD_ID;
      const dueDateColumnId = TASKS_DUE_DATE_COLUMN_ID;
      const personColumnIdForTask = personColumnIdMap?.[tasksBoardId] || personColumnId || DEFAULT_PERSON_COLUMN_ID;

      // Update task name
      await updateItemName({
        boardId: selectedTask.boardId,
        itemId: selectedTask.id,
        name: editTaskName.trim(),
      });

      // Update description if it changed
      if (editTaskDescription !== (selectedTask.description || "")) {
        await updateTextColumn({
          boardId: selectedTask.boardId,
          itemId: selectedTask.id,
          columnId: TASKS_DESCRIPTION_COLUMN_ID_FULL,
          value: editTaskDescription || "",
        });
      }

      // Update date if it changed
      const currentDate = selectedTask.date 
        ? (typeof selectedTask.date === 'string' ? selectedTask.date.split('T')[0] : selectedTask.date instanceof Date ? selectedTask.date.toISOString().split('T')[0] : "")
        : "";
      if (editTaskDate !== currentDate) {
        if (editTaskDate) {
          await updateDateColumn({
            boardId: selectedTask.boardId,
            itemId: selectedTask.id,
            columnId: dueDateColumnId,
            date: editTaskDate,
          });
        } else {
          // Clear date if empty
          await updateDateColumn({
            boardId: selectedTask.boardId,
            itemId: selectedTask.id,
            columnId: dueDateColumnId,
            date: null,
          });
        }
      }

      // Update person if it changed
      const currentPerson = selectedTask.personNames && selectedTask.personNames.length > 0 
        ? selectedTask.personNames[0] 
        : selectedTask.personText || "";
      if (editTaskPerson !== currentPerson && personColumnIdForTask) {
        if (editTaskPerson) {
          await updateDropdownColumn({
            boardId: selectedTask.boardId,
            itemId: selectedTask.id,
            columnId: personColumnIdForTask,
            labels: [editTaskPerson],
          });
        } else {
          // Clear person if empty
          await updateDropdownColumn({
            boardId: selectedTask.boardId,
            itemId: selectedTask.id,
            columnId: personColumnIdForTask,
            labels: [],
          });
        }
      }

      // Update time tracking if it changed
      const currentTimeTracking = selectedTask.timeTracking && String(selectedTask.timeTracking).toLowerCase().trim() === "yes";
      if (editTaskTimeTracking !== currentTimeTracking) {
        await updateStatusColumn({
          boardId: selectedTask.boardId,
          itemId: selectedTask.id,
          columnId: TIME_TRACKING_COLUMN_ID,
          label: editTaskTimeTracking ? "Yes" : "No",
        });
      }

      if (toast) toast.success("Task updated successfully!");
      setIsEditingTask(false);
      handleRefresh();
    } catch (err) {
      console.error("Error updating task:", err);
      if (toast) toast.error("Failed to update task. Please try again.");
    } finally {
      setSavingTask(false);
    }
  }, [selectedTask, editTaskName, editTaskDescription, editTaskDate, editTaskPerson, editTaskTimeTracking, savingTask, personColumnId, personColumnIdMap, handleRefresh]);

  return {
    // State
    selectedTask,
    showPopup,
    reportText,
    setReportText,
    reportImages,
    sendingReport,
    confirmingTask,
    waterReading,
    setWaterReading,
    handleWaterPhotoUpload,
    removeWaterPhoto,
    gasReading,
    setGasReading,
    handleGasPhotoUpload,
    removeGasPhoto,
    electricityReading,
    setElectricityReading,
    readingDates,
    savingReading,
    handleSaveElectricityReading,
    handleSaveWaterReading,
    handleSaveGasReading,
    taskImages,
    setTaskImages,
    handleTaskPhotoUpload,
    removeTaskPhoto,
    taskComments,
    setTaskComments,
    showNewTaskPopup,
    setShowNewTaskPopup,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskDate,
    setNewTaskDate,
    newTaskPerson,
    setNewTaskPerson,
    newTaskProperty,
    setNewTaskProperty,
    newTaskPropertySearch,
    setNewTaskPropertySearch,
    newTaskTimeTracking,
    setNewTaskTimeTracking,
    timeSpent,
    setTimeSpent,
    creatingTask,
    completedMeterReadingData,
    completedTaskData,
    extras,
    setExtras,
    savingExtras,
    handleSaveExtras,
    // TASKS edit/delete
    isEditingTask,
    setIsEditingTask,
    editTaskName,
    setEditTaskName,
    editTaskDescription,
    setEditTaskDescription,
    editTaskDate,
    setEditTaskDate,
    editTaskPerson,
    setEditTaskPerson,
    editTaskTimeTracking,
    setEditTaskTimeTracking,
    savingTask,
    deletingTask,
    handleSaveTask,
    handleDeleteTask,
    propertyDetails,
    // Handlers
    handleTaskClick,
    handleClosePopup,
    handleImageUpload,
    removeImage,
    handleCloseNewTaskPopup,
    handleToggle,
    handleConfirmTask,
    handleSendReport,
    handleCreateTask,
    imageProgress, // Export progress state for UI display
    // PHASE 2 & 3: Export confirmation state and progress
    showConfirmedState,
    confirmationProgress,
  };
}

